import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';

const CompromisoPagoWidget = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [compromiso, setCompromiso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (['estudiante', 'pastor', 'graduado'].includes(user?.rol)) {
      loadCompromiso();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadCompromiso = async () => {
    try {
      const response = await axios.get(`${API}/estudiante/mi-compromiso-pago`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success && response.data.compromiso) {
        setCompromiso(response.data.compromiso);
      }
    } catch (error) {
      console.error('Error loading compromiso:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !compromiso) return;

    setUploading(true);
    try {
      // Convertir a base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        
        const response = await axios.post(
          `${API}/estudiante/compromiso-pago/${compromiso.id}/comprobante`,
          { comprobante_url: base64 },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );

        if (response.data.success) {
          loadCompromiso();
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error subiendo comprobante:', error);
    } finally {
      setUploading(false);
    }
  };

  // No mostrar si no es estudiante/pastor/graduado o no hay compromiso
  if (!user || !['estudiante', 'pastor', 'graduado'].includes(user.rol) || loading) return null;
  if (!compromiso) return null;
  
  // No mostrar si ya está pagado
  if (compromiso.estado === 'pagado') return null;

  const diasRestantes = compromiso.dias_restantes;
  const estaVencido = compromiso.vencido || diasRestantes < 0;
  const estaPorVencer = !estaVencido && diasRestantes <= 3;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-UY', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className={`border-2 ${estaVencido ? 'border-red-400 bg-red-50' : estaPorVencer ? 'border-amber-400 bg-amber-50' : 'border-amber-200 bg-amber-50/50'}`} data-testid="compromiso-pago-widget">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
            estaVencido ? 'bg-red-500' : estaPorVencer ? 'bg-amber-500' : 'bg-amber-400'
          }`}>
            <i className={`fas ${estaVencido ? 'fa-exclamation-triangle' : 'fa-handshake'} text-white text-xl`}></i>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className={`font-bold ${estaVencido ? 'text-red-800' : 'text-amber-800'}`}>
                {estaVencido ? '⚠️ Compromiso de Pago Vencido' : '📋 Compromiso de Pago Pendiente'}
              </h3>
              {compromiso.estado === 'pendiente_verificacion' && (
                <Badge className="bg-blue-500 text-white text-xs">En Revisión</Badge>
              )}
            </div>
            
            <p className={`text-sm ${estaVencido ? 'text-red-700' : 'text-amber-700'} mb-2`}>
              {compromiso.semestre_nombre} - <strong>${compromiso.monto?.toFixed(2)} {compromiso.moneda}</strong>
            </p>
            
            {!estaVencido ? (
              <p className={`text-sm ${estaPorVencer ? 'text-amber-800 font-semibold' : 'text-amber-600'}`}>
                {diasRestantes === 0 ? (
                  <><i className="fas fa-clock mr-1"></i>¡Vence HOY!</>
                ) : diasRestantes === 1 ? (
                  <><i className="fas fa-clock mr-1"></i>Vence mañana</>
                ) : (
                  <><i className="fas fa-calendar mr-1"></i>Vence el {formatDate(compromiso.fecha_vencimiento)} ({diasRestantes} días restantes)</>
                )}
              </p>
            ) : (
              <p className="text-sm text-red-700 font-semibold">
                <i className="fas fa-ban mr-1"></i>
                Tu acceso puede ser bloqueado. Sube tu comprobante cuanto antes.
              </p>
            )}
            
            {/* Acciones */}
            <div className="flex flex-wrap gap-2 mt-3">
              {compromiso.estado === 'pendiente' && (
                <>
                  <label className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer text-sm font-medium transition-colors ${
                    estaVencido 
                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                      : 'bg-amber-600 hover:bg-amber-700 text-white'
                  }`}>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                    {uploading ? (
                      <><i className="fas fa-spinner fa-spin"></i> Subiendo...</>
                    ) : (
                      <><i className="fas fa-upload"></i> Subir Comprobante</>
                    )}
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/app/mis-invoices')}
                    className="text-amber-700 border-amber-300 hover:bg-amber-100"
                  >
                    <i className="fas fa-file-invoice mr-1"></i>
                    Ver Invoice
                  </Button>
                </>
              )}
              
              {compromiso.estado === 'pendiente_verificacion' && (
                <div className="flex items-center gap-2 text-blue-600 text-sm">
                  <i className="fas fa-check-circle"></i>
                  <span>Comprobante enviado - Esperando verificación del administrador</span>
                </div>
              )}
              
              {compromiso.estado === 'rechazado' && (
                <>
                  <div className="w-full text-sm text-red-600 mb-2">
                    <i className="fas fa-times-circle mr-1"></i>
                    Comprobante rechazado. Por favor, sube uno válido.
                  </div>
                  <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors">
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                    {uploading ? (
                      <><i className="fas fa-spinner fa-spin"></i> Subiendo...</>
                    ) : (
                      <><i className="fas fa-redo"></i> Subir Nuevo Comprobante</>
                    )}
                  </label>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompromisoPagoWidget;
